'use client';

/**
 * Inventory modal: list, search, and filter marketplace skills, then attach one
 * to the composer context window.
 *
 * This file is the container: the modal shell plus the attach action. The
 * pieces live in `./skills/`:
 *  - `useSkillCatalog.ts`     — debounce, filters, abortable fetch, selection
 *  - `SkillFilterChips.tsx`   — search field + category / activatable chips
 *  - `SkillList.tsx`          — the selectable result list
 *  - `SkillDetail.tsx`        — the selected skill and the attach button
 */

import React, { useCallback } from 'react';
import { ResponsiveModal } from '@wtc/ui/components/responsive-modal';
import { useGlobalModalStack } from '@wtc/ui/components/global-modal-stack';
import { X } from 'lucide-react';
import type { AttachedSkill } from './skills/types';
import { CHAT_SKILLS_MODAL_ID } from './skills/constants';
import { useSkillCatalog } from './skills/useSkillCatalog';
import { SkillFilterChips } from './skills/SkillFilterChips';
import { SkillList } from './skills/SkillList';
import { SkillDetail } from './skills/SkillDetail';

export { CHAT_SKILLS_MODAL_ID };

export interface ChatSkillsModalProps {
  apiBaseUrl?: string;
  onSelectSkill: (skill: AttachedSkill) => void;
}

export function ChatSkillsModal({
  apiBaseUrl = '/api/meridian',
  onSelectSkill,
}: ChatSkillsModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const open = isModalOpen(CHAT_SKILLS_MODAL_ID);

  const catalog = useSkillCatalog({ apiBaseUrl, open });
  const { selected } = catalog;

  const handleAttach = useCallback(() => {
    // Belt-and-braces with the disabled button: a skill Meridian cannot run is
    // never attachable, however the click arrives.
    if (!selected || !selected.canActivateInMeridian) return;
    onSelectSkill({
      id: selected.id,
      description: selected.description,
      category: selected.category,
    });
    closeModal(CHAT_SKILLS_MODAL_ID);
  }, [selected, onSelectSkill, closeModal]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => {
        if (!next) closeModal(CHAT_SKILLS_MODAL_ID);
      }}
      title="Marketplace skills"
      description="Browse skills and attach one to this message. Meridian will load the playbook into context when you send."
      globalModalId={CHAT_SKILLS_MODAL_ID}
      className="sm:max-w-3xl"
    >
      <div className="flex flex-col gap-3 min-h-[28rem]">
        <SkillFilterChips
          query={catalog.query}
          onQueryChange={catalog.setQuery}
          categories={catalog.categories}
          category={catalog.category}
          onCategoryChange={catalog.setCategory}
          activatableOnly={catalog.activatableOnly}
          onToggleActivatableOnly={catalog.toggleActivatableOnly}
        />

        {catalog.error && (
          <div role="alert" className="text-sm text-destructive">
            {catalog.error}
          </div>
        )}
        {catalog.syncHint && !catalog.error && (
          <p className="text-sm text-muted-foreground">{catalog.syncHint}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-h-0">
          <SkillList
            skills={catalog.skills}
            selectedId={catalog.selectedId}
            onSelect={catalog.setSelectedId}
            loading={catalog.loading}
            total={catalog.total}
          />
          <SkillDetail skill={selected} onAttach={handleAttach} />
        </div>

        <button
          type="button"
          onClick={() => closeModal(CHAT_SKILLS_MODAL_ID)}
          className="self-end inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          Close
        </button>
      </div>
    </ResponsiveModal>
  );
}
